import { Stack } from 'component-library';
import { useEffect } from 'react';

import { R1FactType } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { ItemizedGraphTypeRenderer } from '@/components/Charts/components/ItemizedGraphType';
import { HabitatIntactnessHistorical } from '@/components/Charts/HabitatIntactnessHistorical';
import { NUM_ITEMS_TYPE } from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { GraphFact } from '@/components/Charts/types';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useSetIndicatorHasData } from '@/components/IndicatorCard/IndicatorContextProvider';
import { useTrendLabelFromOrganizationFact } from '@/hooks/useTrendLabelFromFact';
import {
  getBenchmarkForConservationFact,
  useConservationBenchmarksForOrganization,
} from '@/pages/shared/hooks/useBenchmarkForFact';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';
import { NoData } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/NoData';
import { isNullish } from '@/utils/booleans';

export const HabitatIntactnessGraphTile = ({
  plotGroupId,
  graphType,
  zoom,
  setZoom,
  ...props
}: Partial<GraphTileProps>) => {
  const { setIndicatorGraphData } = useSetIndicatorHasData();

  const { [R1FactType.habitat_intactness_total_historic_graph]: habitatIntactnessHistoric } =
    useOrganizationIndicatorGraphFact<GraphFact[]>(
      R1FactType.habitat_intactness_total_historic_graph,
      null,
      undefined,
      plotGroupId,
    );

  const { [R1FactType.habitat_intactness_total]: latestFact } = useOrganizationIndicatorReportingFact<number>(
    R1FactType.habitat_intactness_total,
    null,
    undefined,
    plotGroupId,
  );

  const trendLabel = useTrendLabelFromOrganizationFact({
    fact: R1FactType.habitat_intactness_total_previous_5y_pct_change,
  });

  const habitatIntactnessBenchmarkFact = R1FactType.habitat_intactness_total_benchmark;

  const benchmarks = useConservationBenchmarksForOrganization(habitatIntactnessBenchmarkFact, plotGroupId ?? undefined);
  const benchmark = getBenchmarkForConservationFact({
    fact: latestFact,
    benchmarks,
  });

  const chartData = habitatIntactnessHistoric?.value;

  const noChartData = isNullish(chartData);

  useEffect(() => {
    setIndicatorGraphData({ graphData: !noChartData });
  }, [noChartData, setIndicatorGraphData]);

  if (noChartData) {
    return <NoData />;
  }

  return (
    <Stack center data-testid='habitat-intactness-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        <ItemizedGraphTypeRenderer
          graphType={graphType}
          indicator={GraphTileType.historic_habitat_intactness}
          plotRenderType={props?.plotRenderType ?? NUM_ITEMS_TYPE.TOP_PLOTS}
          zoom={zoom}
          setZoom={setZoom}
          defaultGraphType={
            <HabitatIntactnessHistorical
              data={chartData}
              benchmark={benchmark}
              benchmarks={benchmarks}
              trendLabel={trendLabel}
            />
          }
        />
      </Stack>
    </Stack>
  );
};
